import { AvailableLocale } from '@omnea-digital/toolbox'
import { useQuery } from '@tanstack/react-query'
import { createContext, PropsWithChildren, useEffect, useMemo, useContext } from 'react'
import { I18nextProvider } from 'react-i18next'
import { usePotentiallyAuthenticatedUser } from '@omnea-app/fe-auth/UserSessionProvider'
import { useGetCurrentUser } from '@omnea-app/fe-users-and-groups/useGetCurrentUser'
import { CoreDataCurrency } from '@omnea-app/packages/universal/currency-utils/coreDataCurrency'
import { CurrencyCode } from '@omnea-app/packages/universal/currency-utils/getCurrencyCodes'
import { DateFormatKey } from '@omnea-app/packages/universal/date-utils/dateFormat'
import { Dictionary } from '@omnea-app/packages/universal/utility-types/utilityTypes'
import { determineDateFormat, determineLocale, LocaleSource } from './determineLocaleAndDateFormat'
import { createI18nInstanceWithFallbackResources, getTranslationsJson } from './getFrontendI18n'

export interface PriceData {
  currenciesByCode?: Dictionary<CoreDataCurrency>
  defaultCurrency: CurrencyCode
}

interface TranslationsContext {
  locale: AvailableLocale
  localeSource: LocaleSource
  datePickerFormat: DateFormatKey
  datePickerFormatSource: LocaleSource
  priceData: PriceData
}

const TranslationsContext = createContext<TranslationsContext>({
  get locale(): AvailableLocale {
    throw new Error('Translations context not yet initialized')
  },
  get localeSource(): LocaleSource {
    throw new Error('Translations context not yet initialized')
  },
  get datePickerFormat(): DateFormatKey {
    throw new Error('Translations context not yet initialized')
  },
  get datePickerFormatSource(): LocaleSource {
    throw new Error('Translations context not yet initialized')
  },
  get priceData(): PriceData {
    throw new Error('Translations context not yet initialized')
  },
})

export const useTranslationsContext = () => {
  return useContext(TranslationsContext)
}

export const TranslationsProvider: React.FC<PropsWithChildren<{ priceData: PriceData }>> = ({ children, priceData }) => {
  const { user } = usePotentiallyAuthenticatedUser()
  const { data: userData } = useGetCurrentUser(user?.id)

  const contextValue = useMemo<TranslationsContext>(
    () => ({
      ...determineLocale(userData),
      ...determineDateFormat(userData),
      priceData,
    }),
    [userData, priceData]
  )

  // The locale is only known client-side (user preference), so the static lang from _document is a default
  useEffect(() => {
    document.documentElement.lang = contextValue.locale
  }, [contextValue.locale])

  const i18nData = useQuery({
    queryKey: ['translations-json', contextValue.locale],
    queryFn: () => getTranslationsJson(contextValue.locale),
  }).data

  const i18nInstance = useMemo(() => {
    return createI18nInstanceWithFallbackResources(contextValue.locale, i18nData)
  }, [contextValue.locale, i18nData])

  return (
    <TranslationsContext.Provider value={contextValue}>
      <I18nextProvider i18n={i18nInstance} defaultNS="common">
        {children}
      </I18nextProvider>
    </TranslationsContext.Provider>
  )
}

interface MockTranslationsProviderProps extends PropsWithChildren {
  locale?: AvailableLocale
  priceData?: PriceData
}

const MOCK_PRICE_DATA: PriceData = { defaultCurrency: 'USD' }

export const MockTranslationsProvider: React.FC<MockTranslationsProviderProps> = ({
  children,
  locale = 'en-GB',
  priceData = MOCK_PRICE_DATA,
}) => {
  const contextValue = useMemo<TranslationsContext>(() => {
    return {
      locale,
      localeSource: 'browserLocale',
      datePickerFormat: 'DMY',
      datePickerFormatSource: 'browserLocale',
      priceData,
    }
  }, [locale, priceData])

  const i18nInstance = useMemo(() => {
    return createI18nInstanceWithFallbackResources(contextValue.locale, {})
  }, [contextValue.locale])

  return (
    <TranslationsContext.Provider value={contextValue}>
      <I18nextProvider i18n={i18nInstance} defaultNS="common">
        {children}
      </I18nextProvider>
    </TranslationsContext.Provider>
  )
}
